import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ColorRing } from "react-loader-spinner";
import Pagination from "@mui/material/Pagination";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

// Adjust these imports to match your project structure:
import API_BASE_URL from "../../../../Constants";
import ViewViolationDeviceModal from "./ViewViolationsDeviceModal";
import ListHeader from "../../../../components/ListHeader/NoCardView";
import { tableFields } from "./ViolationFields";

export default function ViolationsList() {
  const [violations, setViolations] = useState([]);
  const [filteredViolations, setFilteredViolations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [searchInput, setSearchInput] = useState("");
  const [searchCriteria, setSearchCriteria] = useState("none");

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedViolationId, setSelectedViolationId] = useState(null);
  const [selectedViolation, setSelectedViolation] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(null);

  const navigate = useNavigate();

  // --------------------------------------
  // 1) Fetch Violations (on mount)
  // --------------------------------------
  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        const resp = await fetch(`${API_BASE_URL}/api/violations`, {
          credentials: "include",
        });
        if (!resp.ok) {
          throw new Error(`Failed to fetch. Status: ${resp.status}`);
        }
        const data = await resp.json();
        const arr = Array.isArray(data) ? data : [];
        setViolations(arr);
        setFilteredViolations(arr);
      } catch (err) {
        console.error("Error fetching violations:", err);
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  // --------------------------------------
  // 2) Searching
  // --------------------------------------
  const handleSearch = () => {
    let data = [...violations];
    if (searchCriteria !== "none" && searchInput.trim()) {
      const lowerVal = searchInput.toLowerCase();
      data = data.filter((vio) => {
        const fieldVal = vio[searchCriteria]?.toString().toLowerCase();
        return fieldVal && fieldVal.includes(lowerVal);
      });
    }
    setFilteredViolations(data);
    setCurrentPage(1);
  };

  // --------------------------------------
  // 3) “Add Violation”
  // --------------------------------------
  const handleOpenCreate = () => {
    navigate("/inspections-violations/violations-entry");
  };

  // --------------------------------------
  // 4) Pagination
  // --------------------------------------
  const totalPages = Math.ceil(filteredViolations.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const displayedViolations = filteredViolations.slice(startIndex, endIndex);

  const handlePageChange = (_, newPage) => setCurrentPage(newPage);
  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  // --------------------------------------
  // 5) “View” => open modal
  // --------------------------------------
  const handleViewClick = (localIndex) => {
    const actualIndex = startIndex + localIndex;
    const rowData = filteredViolations[actualIndex];

    setSelectedViolationId(rowData.violationId);
    setSelectedViolation(rowData);
    setSelectedIndex(actualIndex);
    setIsModalOpen(true);
  };

  // Modal next/prev
  const handleNext = () => {
    if (selectedIndex < filteredViolations.length - 1) {
      const newIndex = selectedIndex + 1;
      const nextData = filteredViolations[newIndex];
      setSelectedIndex(newIndex);
      setSelectedViolationId(nextData.violationId);
      setSelectedViolation(nextData);
    }
  };

  const handlePrev = () => {
    if (selectedIndex > 0) {
      const newIndex = selectedIndex - 1;
      const prevData = filteredViolations[newIndex];
      setSelectedIndex(newIndex);
      setSelectedViolationId(prevData.violationId);
      setSelectedViolation(prevData);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedViolationId(null);
    setSelectedViolation(null);
    setSelectedIndex(null);
  };

  // --------------------------------------
  // 6) Excel Export
  // --------------------------------------
  const handleExcelExport = () => {
    if (!filteredViolations.length) {
      alert("No data to export!");
      return;
    }
    const worksheet = XLSX.utils.json_to_sheet(filteredViolations);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Violations");
    const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([wbout], { type: "application/octet-stream" }), "Violations.xlsx");
  };

  // --------------------------------------
  // RENDER
  // --------------------------------------
  return (
    /**
     * Use h-screen so this container fills the entire viewport.
     * flex-col so children stack vertically (header + table + pagination).
     * bg-white ensures the entire page is white.
     */
    <div className="w-full h-screen flex flex-col bg-white">
      {/* Header / Search / Buttons */}
      <ListHeader
        title="Violations List"
        searchQuery={searchInput}
        handleSearchChange={(e) => setSearchInput(e.target.value)}
        handleSearch={handleSearch}
        handleOpenCreateModal={handleOpenCreate}
      >
        {/* Filter dropdown */}
        <button
          type="button"
          className="px-4 py-2 border-2 border-sss-blue-sel rounded-lg
                     text-sss-blue-sel font-bold hover:bg-sss-blue-sel
                     hover:text-white transition flex items-center"
        >
          <select
            value={searchCriteria}
            onChange={(e) => setSearchCriteria(e.target.value)}
            className="bg-transparent border-none outline-none cursor-pointer"
          >
            <option value="none">No Filter</option>
            <option value="violationNumber">Violation Number</option>
            <option value="agency">Agency</option>
            <option value="buildingSite">Building Site</option>
            <option value="itemStatus">Status</option>
            <option value="violationDate">Violation Date</option>
            <option value="deviceName">Device ID</option>
          </select>
        </button>

        {/* Excel button */}
        <button
          onClick={handleExcelExport}
          className="px-4 py-2 border-2 border-sss-blue-sel rounded-lg
                     text-sss-blue-sel font-bold hover:bg-sss-blue-sel
                     hover:text-white transition flex items-center"
        >
          Excel
        </button>
      </ListHeader>

      {/* 
        This is our table container. 
        flex-1 so it takes the remaining vertical space after the header.
        relative so we can absolutely-position the spinner.
      */}
      <div className="flex-1 relative overflow-x-auto">
        <table className="min-w-full bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200 mt-2">
          <thead className="bg-gray-200">
            <tr>
              {tableFields.map((field) => (
                <th
                  key={field.key}
                  className="px-4 py-2 text-left font-bold text-sss-blue-sel whitespace-nowrap"
                >
                  {field.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="text-gray-700">
            {displayedViolations.length > 0 ? (
              displayedViolations.map((vio, rowIndex) => (
                <tr key={rowIndex} className="border-t hover:bg-gray-50 transition">
                  {tableFields.map((f) => {
                    if (f.key === "actions") {
                      return (
                        <td key={f.key} className="px-4 py-2">
                          <button
                            className="text-sss-info font-semibold hover:underline"
                            onClick={() => handleViewClick(rowIndex)}
                          >
                            View
                          </button>
                        </td>
                      );
                    }
                    return (
                      <td key={f.key} className="px-4 py-2 whitespace-nowrap">
                        {vio[f.key] ?? "N/A"}
                      </td>
                    );
                  })}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={tableFields.length} className="text-center py-4">
                  No violations found.
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {/* 
          If loading, overlay the spinner in the middle of the table area.
          absolute inset-0 + flex center => spinner is centered.
          bg-white/90 => white translucent overlay so table is grayed out.
        */}
        {isLoading && (
          <div className="inset-0 flex items-center justify-center bg-white/50 z-50">
            <ColorRing
              visible
              height={200}
              width={200}
              ariaLabel="loading-indicator"
              colors={["#033B5C", "#033B5C", "#033B5C", "#033B5C", "#033B5C"]}
            />
          </div>
        )}
      </div>

      {/* Pagination pinned at bottom */}
      <div
        className="fixed bottom-0 left-0 w-full bg-white border-t
                   border-gray-300 p-4 flex justify-between items-center"
      >
        <Pagination
          count={totalPages}
          page={currentPage}
          onChange={handlePageChange}
          color="primary"
          shape="rounded"
        />
        <div className="flex items-center space-x-2">
          <span className="font-semibold text-gray-700">Rows per page:</span>
          <select
            className="border border-gray-300 rounded px-2 py-1 focus:outline-none"
            value={rowsPerPage}
            onChange={handleRowsPerPageChange}
          >
            <option value="5">5</option>
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
          </select>
        </div>
      </div>

      {/* “View” modal */}
      {isModalOpen && selectedViolationId && (
        <ViewViolationDeviceModal
          open={isModalOpen}
          onClose={handleCloseModal}
          violationId={selectedViolationId}
          prefilledData={selectedViolation}
          setNext={handleNext}
          setPrev={handlePrev}
        />
      )}
    </div>
  );
}
